import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Pagination, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaCalendarAlt, FaSync, FaTimes, FaDollarSign, FaFileAlt, FaArrowLeft } from 'react-icons/fa';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/apiService';
import Breadcrumb from '../components/Breadcrumb';
import 'bootstrap/dist/css/bootstrap.min.css';

const SalesPersonCommissions = () => {
  const { salespersonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [commissions, setCommissions] = useState([]);
  const [salesPerson, setSalesPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filter state
  const [dateFilter, setDateFilter] = useState('current_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [month, setMonth] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    dateFilter: 'current_month',
    startDate: '',
    endDate: '',
    month: ''
  });

  // Initialize current month
  useEffect(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setMonth(currentMonth);
    setAppliedFilters((prev) => ({ ...prev, month: currentMonth }));
  }, []);

  // Apply filters from URL query params if present
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const qDateFilter = qs.get('dateFilter');
    const qMonth = qs.get('month');
    const qStartDate = qs.get('startDate');
    const qEndDate = qs.get('endDate');

    if (qDateFilter) setDateFilter(qDateFilter);
    if (qMonth) setMonth(qMonth);
    if (qStartDate) setStartDate(qStartDate);
    if (qEndDate) setEndDate(qEndDate);

    if (qDateFilter || qMonth || qStartDate || qEndDate) {
      const nextFilters = {
        dateFilter: qDateFilter || 'current_month',
        startDate: qStartDate || '',
        endDate: qEndDate || '',
        month: qMonth || month
      };
      setAppliedFilters(nextFilters);
      setCurrentPage(1);
    }
  }, [location.search]);

  // Fetch commissions data
  const fetchCommissions = async (page = 1, filters = appliedFilters) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getSalesPersonCommissions(
        salespersonId, 
        page, 
        pageSize, 
        filters
      );
      
      if (response && response.results) {
        setCommissions(response.results);
        setTotalCount(response.count || 0);
        setTotalPages(Math.ceil((response.count || 0) / pageSize));
        
        // Set salesperson info from response
        if (response.salesperson) {
          setSalesPerson(response.salesperson);
        }
      } else {
        setCommissions([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching commissions:', err);
      setError('Failed to load commissions. Please try again.');
      setCommissions([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchCommissions(currentPage, appliedFilters);
  }, [currentPage, appliedFilters, salespersonId]);

  // Handle filter application
  const handleApplyFilters = () => {
    const newFilters = {
      dateFilter,
      startDate,
      endDate,
      month
    };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    setDateFilter('current_month');
    setStartDate('');
    setEndDate('');
    setMonth(currentMonth);
    
    const resetFilters = {
      dateFilter: 'current_month',
      startDate: '',
      endDate: '',
      month: currentMonth
    };
    setAppliedFilters(resetFilters);
    setCurrentPage(1);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchCommissions(currentPage, appliedFilters);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/salespersons');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle pure date strings (YYYY-MM-DD) explicitly to avoid timezone shifts
      const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(dateString);
      const opts = { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' };
      if (m) {
        const [, y, mo, d] = m;
        const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
        return new Intl.DateTimeFormat('en-US', opts).format(date);
      }
      // Default: format in UTC to keep the calendar day stable across timezones
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', opts).format(date);
    } catch (e) {
      return dateString;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  // Format percent (rate values like base_rate, bonus_rate, commission_rate)
  const formatPercent = (rate) => {
    if (rate === null || rate === undefined) return '0%';
    const n = Number(rate);
    if (Number.isNaN(n)) return `${rate}`;
    return `${(n * 100).toFixed(2)}%`;
  };

  // Compute total commission for this salesperson over currently loaded (filtered) commissions
  const getFilteredTotalCommission = () => {
    if (!Array.isArray(commissions) || commissions.length === 0) return 0;
    const sid = Number(salespersonId);
    return commissions.reduce((sum, c) => {
      let amount = 0;
      if (Array.isArray(c.salesperson_entries) && c.salesperson_entries.length > 0) {
        const match = c.salesperson_entries.find(sp => Number(sp.sales_person_id) === sid);
        amount = match && match.commission_amount ? Number(match.commission_amount) : 0;
      } else if (Array.isArray(c.sales_person_data) && c.sales_person_data.length > 0) {
        const match = c.sales_person_data.find(sp => Number(sp.sales_person_id) === sid);
        amount = match && match.commission_amount ? Number(match.commission_amount) : 0;
      } else if (c.commission_per_sales_person) {
        amount = Number(c.commission_per_sales_person);
      }
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
  };

  // Compute latest closed date across loaded commissions
  const getLatestClosedDate = () => {
    if (!Array.isArray(commissions) || commissions.length === 0) return null;
    const dates = commissions
      .map(c => c.order_closed_date)
      .filter(Boolean)
      .map(d => new Date(d));
    if (dates.length === 0) return null;
    const latest = new Date(Math.max.apply(null, dates));
    return latest.toISOString();
  };

  // Get filter description
  const getFilterDescription = () => {
    const { dateFilter, startDate, endDate, month } = appliedFilters;
    
    if (dateFilter === 'current_month') {
      return 'Current Month';
    } else if (dateFilter === 'month' && month) {
      const monthDate = new Date(month + '-01');
      return monthDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    } else if (dateFilter === 'date_range' && startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    return 'All Time';
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return (
      <Pagination className="justify-content-center mt-3">
        <Pagination.First 
          onClick={() => handlePageChange(1)} 
          disabled={currentPage === 1}
        />
        <Pagination.Prev 
          onClick={() => handlePageChange(currentPage - 1)} 
          disabled={currentPage === 1}
        />
        
        {startPage > 1 && (
          <>
            <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
            {startPage > 2 && <Pagination.Ellipsis />}
          </>
        )}
        
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
          <Pagination.Item
            key={page}
            active={page === currentPage}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </Pagination.Item>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <Pagination.Ellipsis />}
            <Pagination.Item onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>
          </>
        )}
        
        <Pagination.Next 
          onClick={() => handlePageChange(currentPage + 1)} 
          disabled={currentPage === totalPages}
        />
        <Pagination.Last 
          onClick={() => handlePageChange(totalPages)} 
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  return (
    <Container fluid className="px-4 py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <style jsx>{`
        .page-title {
          color: #2c3e50;
          font-weight: 700;
          font-size: 2rem;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .page-subtitle {
          font-size: 1rem;
          color: #6c757d;
        }
        
        .commissions-container {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 2rem;
          color: white;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        
        .stats-card {
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 15px;
          padding: 1.5rem;
          color: white;
          transition: transform 0.3s ease;
        }
        
        .stats-card:hover {
          transform: translateY(-5px);
        }
        
        .main-card {
          border: none;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        
        .card-header-custom {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 1.5rem 2rem;
        }
        
        .table-modern {
          width: 100%;
          margin-bottom: 0;
          table-layout: auto;
        }
        
        .table-responsive {
          overflow-x: auto !important;
          overflow-y: visible;
        }
        
        .table-modern thead th {
          background-color: #f8f9fa;
          border: none;
          font-weight: 800;
          color: #495057;
          padding: 12px 15px;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          white-space: nowrap;
          border-bottom: 2px solid #e9ecef;
        }
        
        .table-modern tbody tr {
          border: none;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .table-modern tbody tr:hover {
          background-color: #f8f9fa;
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }
        
        .table-modern tbody td {
          padding: 15px;
          border: none;
          vertical-align: middle;
          border-bottom: 1px solid #f1f3f4;
          word-wrap: break-word;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        /* Per-column widths tuned to real data */
        .table-modern thead th, .table-modern tbody td { box-sizing: border-box; }
        .table-modern thead th:nth-child(1), .table-modern tbody td:nth-child(1) { min-width: 140px; }
        .table-modern thead th:nth-child(2), .table-modern tbody td:nth-child(2) { min-width: 450px; }
        .table-modern thead th:nth-child(3), .table-modern tbody td:nth-child(3) { min-width: 180px; }
        .table-modern thead th:nth-child(4), .table-modern tbody td:nth-child(4) { min-width: 150px; }
        .table-modern thead th:nth-child(5), .table-modern tbody td:nth-child(5) { min-width: 150px; }
        .table-modern thead th:nth-child(6), .table-modern tbody td:nth-child(6) { min-width: 140px; }
        .table-modern thead th:nth-child(7), .table-modern tbody td:nth-child(7) { min-width: 140px; }
        .table-modern thead th:nth-child(8), .table-modern tbody td:nth-child(8) { min-width: 220px; }
        .table-modern thead th:nth-child(9), .table-modern tbody td:nth-child(9) { min-width: 200px; }
        
        .loading-container {
          padding: 4rem 2rem;
          text-align: center;
        }
        
        .loading-spinner {
          width: 3rem;
          height: 3rem;
          border-width: 0.3rem;
        }
        
        .empty-state {
          padding: 4rem 2rem;
          text-align: center;
          color: #6c757d;
        }
        
        .empty-state-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }
        
        .pagination-container {
          background-color: #f8f9fa;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e9ecef;
        }
        
        .pagination-info {
          color: #6c757d;
          font-weight: 500;
        }
        
        .filter-controls {
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(10px);
          border-radius: 15px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          border: 1px solid rgba(255,255,255,0.2);
        }
        
        .btn-modern {
          border-radius: 10px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
          border: none;
        }
        
        .btn-modern:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0,0,0,0.2);
        }
        
        .form-control-modern {
          border-radius: 10px;
          border: 2px solid #e9ecef;
          padding: 0.75rem 1rem;
          transition: all 0.3s ease;
        }
        
        .form-control-modern:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }
        
        .back-button {
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 10px;
          color: white;
          padding: 0.75rem 1.5rem;
          transition: all 0.3s ease;
        }
        
        .back-button:hover {
          background: rgba(255,255,255,0.2);
          color: white;
          transform: translateY(-2px);
        }
      `}</style>
      
      <Breadcrumb 
        items={[
          { label: 'Sales Persons', href: '/salespersons' },
          { label: salesPerson?.name || `Sales Person ${salespersonId}`, active: true }
        ]} 
      />

      {/* Page Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-title mb-1">
                {salesPerson?.name ? `${salesPerson.name}'s Commissions` : 'Sales Person Commissions'}
              </h2>
              <p className="page-subtitle text-muted mb-0">
                View and manage commission records for this salesperson
              </p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="btn-group" role="group">
                <Button 
                  variant={showFilters ? "primary" : "outline-primary"}
                  onClick={() => setShowFilters(!showFilters)}
                  className="d-flex align-items-center gap-2"
                >
                  {showFilters ? 'Filters' : 'Filters'}
                  <i className="fas fa-filter"></i>
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      {/* Stats Summary */}
      {salesPerson && (
        <Row className="mb-4">
          <Col md={4} className="mb-3">
            <Card className="border-0 shadow-sm h-100" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <Card.Body className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="text-white-50 small text-uppercase fw-bold mb-1">
                    Total Commissions
                  </div>
                  <div className="h4 mb-0 fw-bold">
                    {formatCurrency(getFilteredTotalCommission())}
                  </div>
                </div>
                <div className="ms-3">
                  <i className="fas fa-chart-line fa-2x opacity-75"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4} className="mb-3">
            <Card className="border-0 shadow-sm h-100" style={{
              background: 'linear-gradient(135deg, rgb(184, 101, 156) 0%, rgb(174, 32, 160) 100%)',
              color: 'white'
            }}>
              <Card.Body className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="text-white-50 small text-uppercase fw-bold mb-1">
                    Total Closings
                  </div>
                  <div className="h4 mb-0 fw-bold">
                    {totalCount}
                  </div>
                </div>
                <div className="ms-3">
                  <i className="fas fa-file-alt fa-2x opacity-75"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4} className="mb-3">
            <Card className="border-0 shadow-sm h-100" style={{
              background: 'linear-gradient(135deg, #23bdee 0%, #8bd2f0 100%)',
              color: 'white'
            }}>
              <Card.Body className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="text-white-50 small text-uppercase fw-bold mb-1">
                    Latest Closing
                  </div>
                  <div className="h5 mb-0 fw-bold">
                    {formatDate(getLatestClosedDate()) || 'N/A'}
                  </div>
                </div>
                <div className="ms-3">
                  <i className="fas fa-calendar-check fa-2x opacity-75"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    
      {/* Enhanced Professional Filter Section */}
      {showFilters && (
      <Card 
        className="mb-4 filter-card" 
        style={{ 
          border: 'none', 
          boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          animation: 'slideDown 0.3s ease-out'
        }}
      >
        <Card.Header 
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '16px 16px 0 0',
            padding: '1rem 1.5rem'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className="fas fa-sliders-h me-2"></i>
              <h6 className="mb-0 fw-bold">Filters</h6>
            </div>
          </div>
        </Card.Header>
        <Card.Body style={{ padding: '1rem 1rem 2rem 1rem !important' }}>
          <Row className="align-items-center g-4 mb-2">
            <Col lg={3}>
              <div className="filter-controls">
                <label className="form-label fw-semibold text-muted mb-2">
                  Filter Duration
                </label>
                <div className="position-relative">
                  <Form.Select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="form-select-modern"
                    style={{
                      border: '2px solid #e9ecef',
                      borderRadius: '12px',
                      fontSize: '0.95rem',
                      padding: '0.75rem 1rem',
                      background: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e9ecef';
                      e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                    }}
                  >
                    <option value="current_month">Current Month</option>
                    <option value="month">Specific Month</option>
                    <option value="date_range">Date Range</option>
                  </Form.Select>
                  <i className="fas fa-chevron-down position-absolute" 
                     style={{
                       right: '1rem',
                       top: '50%',
                       transform: 'translateY(-50%)',
                       pointerEvents: 'none',
                       color: '#6c757d',
                       fontSize: '0.8rem'
                     }}>
                  </i>
                </div>
              </div>
            </Col>
            
            {dateFilter === 'month' && (
              <Col lg={3}>
                <div className="filter-controls">
                  <label className="form-label fw-semibold text-muted mb-2">
                    Month
                  </label>
                  <Form.Control
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    style={{
                      border: '2px solid #e9ecef',
                      borderRadius: '12px',
                      fontSize: '0.95rem',
                      padding: '0.75rem 1rem',
                      background: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e9ecef';
                      e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                    }}
                  />
                </div>
              </Col>
            )}
            
            {dateFilter === 'date_range' && (
              <>
                <Col lg={3}>
                  <div className="filter-controls">
                    <label className="form-label fw-semibold text-muted mb-2">
                      Start Date
                    </label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{
                        border: '2px solid #e9ecef',
                        borderRadius: '12px',
                        fontSize: '0.95rem',
                        padding: '0.75rem 1rem',
                        background: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e9ecef';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                      }}
                    />
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="filter-controls">
                    <label className="form-label fw-semibold text-muted mb-2">
                      End Date
                    </label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={{
                        border: '2px solid #e9ecef',
                        borderRadius: '12px',
                        fontSize: '0.95rem',
                        padding: '0.75rem 1rem',
                        background: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e9ecef';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                      }}
                    />
                  </div>
                </Col>
              </>
            )}
          </Row>
          
          {/* Filter Actions */}
          <Row className="mt-0 pt-3 pb-2" style={{ borderTop: '1px solid #e9ecef', minHeight: '60px' }}>
            <Col>
              <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 h-100">
                <div className="filter-actions d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
                  <button 
                    className="btn btn-outline-primary btn-sm me-2"
                    onClick={handleResetFilters}
                    style={{ 
                      borderRadius: '8px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="fas fa-refresh me-1"></i>
                    Reset
                  </button>
                  <button 
                    className="btn btn-primary btn-sm me-2"
                    onClick={handleApplyFilters}
                    style={{ 
                      borderRadius: '8px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="fas fa-calendar-alt me-1"></i>
                    Apply
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleRefresh}
                    disabled={loading}
                    style={{ 
                      borderRadius: '8px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className={`fas fa-sync-alt me-1 ${loading ? 'fa-spin' : ''}`}></i>
                    Refresh
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      )}

      {/* Main Content */}
      <Card className="main-card">
        <div className="card-header-custom d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold">Commission Records</h5>
          {/* <Badge bg="light" text="dark" className="px-3 py-2">
            {getFilterDescription()}
          </Badge> */}
        </div>
        <Card.Body className="p-0">
          {loading ? (
            <div className="loading-container">
              <Spinner animation="border" role="status" className="loading-spinner text-primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3 text-muted">Loading commissions...</p>
            </div>
          ) : error ? (
            <Alert variant="danger" className="m-4">
              <Alert.Heading>Error Loading Data</Alert.Heading>
              {error}
            </Alert>
          ) : commissions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <FaFileAlt />
              </div>
              <h5>No Commissions Found</h5>
              <p className="text-muted">
                No commission records found for the selected period.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-modern">
                <thead>
                  <tr>
                    <th>Order Number</th>
                    <th>Commission Amount</th>
                    <th>Total Permium Amount</th>
                    <th>Purchase Price</th>
                    <th>Loan Amount</th>
                    <th>Open Date</th>
                    <th>Closed Date</th>
                    <th>Transaction Coordinator</th>
                    <th>Processor</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((commission) => (
                    <tr key={commission.id}>
                      <td>
                        <Badge bg="primary" className="px-3 py-2">{commission.order_number}</Badge>
                      </td>
                      <td>
                        {Array.isArray(commission.salesperson_entries) && commission.salesperson_entries.length > 0 ? (
                          <div className="d-flex flex-column gap-2">
                            {commission.salesperson_entries
                              .filter(sp => Number(sp.sales_person_id) === Number(salespersonId))
                              .map((sp, idx) => (
                              <div key={`${commission.id || commission.order_number}-sp-${idx}`} className="d-flex flex-column">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  {/* {(Number(commission.sales_person_count) || 0) > 1 && (
                                    <span className="fw-semibold">{sp.sales_person_name || sp.name || 'Sales Person'}</span>
                                  )} */}
                                  <span className="fw-bold">{formatCurrency(sp.commission_amount)}</span>
                                  <Badge bg={sp.is_bonus_applied ? 'success' : 'secondary'} pill>
                                    {formatPercent(sp.commission_rate)}
                                  </Badge>
                                  {/* {typeof sp.is_bonus_applied === 'boolean' && ( */}
                                    { sp.is_bonus_applied == true && (
                                    <Badge bg={sp.is_bonus_applied ? 'success' : 'secondary'} pill>
                                      {sp.is_bonus_applied ? 'HiLi' : 'No bonus'}
                                    </Badge>)
                                    }
                                  {/* )} */}
                                </div>
                                <div className="text-muted small d-flex flex-wrap align-items-center gap-2">
                                  {sp.base_rate !== undefined && (
                                    <span className="text-secondary">Base {formatPercent(sp.base_rate)}</span>
                                  )}
                                  {sp.bonus_rate !== undefined && (
                                    <span className="text-primary">HiLi {formatPercent(sp.bonus_rate)}</span>
                                  )}
                                  {sp.threshold_value !== undefined && sp.threshold_value !== null && (
                                    <span className="text-warning">• Threshold {formatCurrency(sp.threshold_value)}</span>
                                  )}
                                  
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : Array.isArray(commission.sales_person_data) && commission.sales_person_data.length > 0 ? (
                          <div className="d-flex flex-column gap-2">
                            {commission.sales_person_data.map((sp, idx) => (
                              <div key={`${commission.id || commission.order_number}-spd-${idx}`} className="d-flex flex-column">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  {(Number(commission.sales_person_count) || 0) > 1 && (
                                    <span className="fw-semibold">{sp.sales_person_name || sp.name || 'Sales Person'}</span>
                                  )}
                                  {sp.commission_amount !== undefined && (
                                    <span className="fw-bold">{formatCurrency(sp.commission_amount)}</span>
                                  )}
                                  {sp.commission_rate !== undefined && (
                                    <Badge bg={sp.is_bonus_applied ? 'success' : 'secondary'} pill>
                                      {formatPercent(sp.commission_rate)}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-muted small d-flex flex-wrap align-items-center gap-2">
                                  {sp.base_rate !== undefined && (
                                    <span className="text-secondary">Base {formatPercent(sp.base_rate)}</span>
                                  )}
                                  {sp.bonus_rate !== undefined && (
                                    <span className="text-primary">HiLi {formatPercent(sp.bonus_rate)}</span>
                                  )}
                                  {sp.threshold_value !== undefined && sp.threshold_value !== null && (
                                    <span className="text-warning">• Threshold {formatCurrency(sp.threshold_value)}</span>
                                  )}
                                  {typeof sp.is_bonus_applied === 'boolean' && (
                                    <Badge bg={sp.is_bonus_applied ? 'success' : 'secondary'} pill>
                                      {sp.is_bonus_applied ? 'HiLi applied' : 'No bonus'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Fallback to legacy aggregate value
                          <span>{formatCurrency(commission.commission_per_sales_person)}</span>
                        )}
                      </td>
                      <td>
                        {(commission.total_net)}
                        </td>

                      <td>
                        {formatCurrency(commission.purchase_price)}
                      </td>
                      <td>
                        {formatCurrency(commission.loan_amount)}
                      </td>
                      <td className="text-muted">
                        {formatDate(commission.order_open_date)}
                      </td>
                      <td className="text-muted">
                        {formatDate(commission.order_closed_date)}
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '220px' }}>
                          {commission.transaction_coordinator || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '200px' }}>
                          {commission.processor || 'N/A'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <Row className="align-items-center">
              <Col md={6}>
                <div className="pagination-info">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries
                </div>
              </Col>
              <Col md={6}>
                {renderPagination()}
              </Col>
            </Row>
          </div>
        )}
      </Card>
    </Container>
  );
};

export default SalesPersonCommissions;